import { type GqlExecutor } from '@hopdrive/sdk-core'
import { getCustomerConfig as getCustomerConfigQuery } from '@gql/operations'
import type { GetCustomerConfigQueryVariables } from '@gql/schema'
import { filterConfigByRole, enrichMetadata } from '../utils/filter-by-role.js'
import type { EffectiveConfig, SqlMetadata } from '../types.js'
import type { HopDriveConfig } from '../_generated/hopdrive-config-schema.js'
import { LEVELS, type Role } from '../types.js'
import { EntityNotFoundError } from '../errors.js'

export interface GetCustomerConfigOptions {
  /** Filter config to only include settings readable by this role */
  role?: Role
  /** Include enriched metadata with settings info */
  includeMetadata?: boolean
}

/**
 * Get effective config for a customer.
 * Uses SQL effective_config computed field which merges:
 * hopdrive + region + organization + customer
 */
export async function getCustomerConfig(params: {
  customerId: GetCustomerConfigQueryVariables['customerId']
  executor?: GqlExecutor
  options?: GetCustomerConfigOptions
}): Promise<EffectiveConfig> {
  const { customerId, options } = params
  const includeMetadata = options?.includeMetadata ?? false

  const customer = await getCustomerConfigQuery(
    { customerId, includeMetadata },
    { executor: params.executor }
  )

  if (!customer) {
    throw new EntityNotFoundError(LEVELS.CUSTOMER, customerId)
  }

  const sqlResponse = customer.effective_config as HopDriveConfig & {
    __metadata?: SqlMetadata
  }

  // Extract __metadata and raw config
  const { __metadata, ...rawConfig } = sqlResponse

  // Filter by role (removes settings not readable by role)
  const filteredConfig = filterConfigByRole(rawConfig, options?.role)

  // If metadata requested, enrich with TypeScript setting definitions
  if (includeMetadata && __metadata) {
    const enrichedMetadata = enrichMetadata(__metadata, filteredConfig)
    return {
      ...filteredConfig,
      __metadata: enrichedMetadata,
    }
  }

  // Return config without metadata
  return filteredConfig
}
